/**
 * Choco Select Component
 * A standardized select component that follows Choco's design system.
 * Built on top of HeroUI's Select with consistent styling and accessibility.
 */

import { Select, SelectItem, type SelectProps } from '@heroui/react';
import React from 'react';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface ChocoSelectProps
  extends Omit<SelectProps, 'children' | 'onSelectionChange'> {
  /** Array of options to display */
  options: SelectOption[];
  /** Current selected value */
  value?: string;
  /** Callback when selection changes */
  onSelectionChange?: (value: string | null) => void;
  /** Label for the select */
  label?: string;
  /** Description text that appears below the label */
  description?: string;
  /** Error message to display */
  errorMessage?: string;
  /** Whether the select is required */
  isRequired?: boolean;
  /** Whether to show descriptions for options */
  showDescriptions?: boolean;
  /** Custom class name for the select wrapper */
  className?: string;
}

/**
 * A standardized select component that follows Choco's design system.
 *
 * @example
 * ```tsx
 * <ChocoSelect
 *   label="Chat Type"
 *   placeholder="Select chat type..."
 *   options={[
 *     { value: 'ordering', label: 'Ordering', description: 'Standard ordering chat' },
 *     { value: 'support', label: 'Support', description: 'Support chat' }
 *   ]}
 *   value={selectedValue}
 *   onSelectionChange={handleSelectionChange}
 *   isRequired
 * />
 * ```
 */
export const ChocoSelect: React.FC<ChocoSelectProps> = ({
  options,
  value,
  onSelectionChange,
  label,
  description,
  errorMessage,
  isRequired = false,
  showDescriptions = true,
  className = '',
  ...props
}) => {
  const handleSelectionChange = (keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;

    const selectedKey = Array.from(keys)[0] as string;
    onSelectionChange?.(selectedKey || null);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
          {isRequired && <span className="text-danger ml-1">*</span>}
        </label>
      )}

      {description && (
        <p className="text-xs text-foreground-600">{description}</p>
      )}

      <Select
        selectedKeys={value ? [value] : []}
        onSelectionChange={handleSelectionChange}
        errorMessage={errorMessage}
        isInvalid={!!errorMessage}
        variant="bordered"
        classNames={{
          trigger:
            'bg-default-50 border-default-200 hover:border-default-300 focus-within:border-primary shadow-sm hover:shadow-md transition-all duration-200',
          selectorIcon: 'right-3',
          label: 'text-foreground-700',
          value: 'text-foreground',
        }}
        {...props}
      >
        {options.map((option) => (
          <SelectItem
            key={option.value}
            textValue={option.label}
            isDisabled={option.disabled}
            className="text-left py-3 border-b border-divider last:border-b-0"
          >
            <div className="py-1">
              <div className="font-medium text-foreground">{option.label}</div>
              {showDescriptions && option.description && (
                <div className="text-xs text-foreground-600 mt-1">
                  {option.description}
                </div>
              )}
            </div>
          </SelectItem>
        ))}
      </Select>
    </div>
  );
};
